import { ROUTE_FAQS, ROUTE_HOWTO_STEPS } from "./seo-content";
import { EN_ROUTE_FAQS, EN_ROUTE_HOWTO_STEPS } from "./seo-content-en";
import { GLOSSARY_TERMS } from "./seo-glossary";
import { EN_GLOSSARY_TERMS } from "./seo-glossary-en";
import { ALL_FAQS } from "./seo-faq";
import { EN_ALL_FAQS, EN_FAQ_GROUPS } from "./seo-faq-en";
import { COMPARE_PAGES, type ComparePage } from "./seo-compare";
import { EN_COMPARE_PAGES, type EnglishComparePage } from "./seo-compare-en";
import { EN_CASE_STUDIES } from "./seo-case-studies-en";
import { BLOG_POSTS, getOrderedBlogPosts } from "./seo-blog";
import { INDEXABLE_MARKETPLACE_DETAIL_ROUTES, buildMarketplaceDetailExceptionsNginx, getIndexableMarketplaceDetail } from "./marketplace-seo";

type SeoStructuredDataNode = Record<string, unknown>;

type SitemapChangefreq = "always" | "hourly" | "daily" | "weekly" | "monthly" | "yearly" | "never";

type Hreflang = "zh-Hans" | "en" | "x-default";
type RouteLocale = "zh-Hans" | "en";

type SeoRouteEntry = {
  pathPrefix: string;
  canonicalPath?: string;
  title: string;
  description: string;
  ogImagePath?: string;
  ogImageAlt?: string;
  robots?: "index,follow" | "noindex,follow";
  prerender?: boolean;
  includeInSitemap?: boolean;
  ogType?: "website" | "article";
  category?:
    | "home"
    | "marketplace"
    | "marketplace-detail"
    | "docs"
    | "article"
    | "tooling"
    | "demo"
    | "glossary"
    | "faq"
    | "pricing"
    | "compare"
    | "blog-index"
    | "blog-post";
  breadcrumbs?: Array<{ name: string; path: string }>;
  changefreq?: SitemapChangefreq;
  priority?: number;
  locale?: RouteLocale;
  /**
   * Hreflang 语言版本对应的 path（含 trailing slash）。
   * 当前阶段仅声明 zh-Hans + x-default 都指向自身；C2 阶段 EN 镜像页上线后，
   * 在 staticRouteSeoEntries 对应条目上 push { hreflang: "en", path: "/en/..." } 即可，
   * 全站的 <link rel="alternate"> 与 sitemap xhtml:link 会自动同步。
   */
  alternates?: Array<{ hreflang: Hreflang; path: string }>;
};

export type AlternateLink = { hreflang: string; href: string };

export type RouteSeoDocument = {
  title: string;
  description: string;
  canonicalUrl: string;
  htmlLang: string;
  ogLocale: string;
  ogImageUrl: string;
  ogImageAlt: string;
  ogType: "website" | "article";
  robots: "index,follow" | "noindex,follow";
  structuredData: string;
  alternateLinks: AlternateLink[];
};

export const SITE_ORIGIN = "https://callanything.xyz";
export const SITE_NAME = "CALL ANYTHING";
const OG_IMAGE_VERSION = "20260423-1";
const DEFAULT_OG_IMAGE_ALT = "CALL ANYTHING 官网分享卡片";
const ORGANIZATION_ID = `${SITE_ORIGIN}/#organization`;
const WEBSITE_ID = `${SITE_ORIGIN}/#website`;

const DEFAULT_TITLE = "CALL ANYTHING — Agent 调用外部能力的开放协议";
const DEFAULT_DESCRIPTION =
  "任何能力都能挂上来，任何 Agent 都能调进去。一份 Hotline = 一段标准化的 Caller↔Responder 通信契约；身份、计费、审批、可观测、路由 —— 全在协议里。";
const DEFAULT_EN_TITLE = "CALL ANYTHING | Open protocol for callable external capabilities";
const DEFAULT_EN_DESCRIPTION =
  "CALL ANYTHING is an open protocol and marketplace for AI agents to discover, call, and settle external capabilities through standardized Hotlines.";

function normalizeMatchPath(pathname: string) {
  if (pathname === "" || pathname === "/") {
    return "/";
  }

  return pathname.replace(/\/+$/, "") || "/";
}

function withTrailingSlash(pathname: string) {
  const normalized = normalizeMatchPath(pathname);
  if (normalized === "/") {
    return "/";
  }

  return `${normalized}/`;
}

function mirrorAlternates(zhPath: string, enPath: string) {
  return {
    zh: [{ hreflang: "en" as const, path: enPath }],
    en: [
      { hreflang: "en" as const, path: enPath },
      { hreflang: "zh-Hans" as const, path: zhPath },
      { hreflang: "x-default" as const, path: zhPath },
    ],
  };
}

const HOME_MIRROR_ALTERNATES = mirrorAlternates("/", "/en/");
const DOCS_MIRROR_ALTERNATES = mirrorAlternates("/docs/", "/en/docs/");
const QUICK_START_CALLER_MIRROR_ALTERNATES = mirrorAlternates("/docs/quick-start-caller/", "/en/docs/quick-start-caller/");
const QUICK_START_RESPONDER_MIRROR_ALTERNATES = mirrorAlternates(
  "/docs/quick-start-responder/",
  "/en/docs/quick-start-responder/"
);
const PROTOCOL_EXAMPLES_MIRROR_ALTERNATES = mirrorAlternates(
  "/docs/protocol-examples/",
  "/en/docs/protocol-examples/"
);
const PROTOCOL_ARCHITECTURE_MIRROR_ALTERNATES = mirrorAlternates(
  "/docs/protocol-architecture/",
  "/en/docs/protocol-architecture/"
);
const DEPLOYABILITY_PROFILES_MIRROR_ALTERNATES = mirrorAlternates(
  "/docs/deployability-profiles/",
  "/en/docs/deployability-profiles/"
);
const GLOSSARY_MIRROR_ALTERNATES = mirrorAlternates("/glossary/", "/en/glossary/");
const FAQ_MIRROR_ALTERNATES = mirrorAlternates("/faq/", "/en/faq/");
const SEARCH_MIRROR_ALTERNATES = mirrorAlternates("/search/", "/en/search/");
const TRUST_SETTLEMENT_MIRROR_ALTERNATES = mirrorAlternates("/trust-settlement/", "/en/trust-settlement/");
const VISION_MIRROR_ALTERNATES = mirrorAlternates("/vision/", "/en/vision/");
const OPC_MIRROR_ALTERNATES = mirrorAlternates("/opc/", "/en/opc/");
const MARKETPLACE_ROADMAP_MIRROR_ALTERNATES = mirrorAlternates("/marketplace-roadmap/", "/en/marketplace-roadmap/");

function buildSearchTargetUrl(path: string) {
  return `${new URL(path, SITE_ORIGIN).toString()}?q={search_term_string}`;
}

function websiteGraph(languageTag: RouteLocale) {
  const isEnglish = languageTag === "en";
  return {
    "@type": "WebSite",
    "@id": WEBSITE_ID,
    url: `${SITE_ORIGIN}/`,
    name: SITE_NAME,
    inLanguage: languageTag,
    description: isEnglish
      ? "CALL ANYTHING is an open protocol and marketplace for AI agents to discover, call, and settle external capabilities."
      : "CALL ANYTHING 是一个让 Agent 调用外部能力的开放协议与 Marketplace。",
    publisher: { "@id": ORGANIZATION_ID },
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: buildSearchTargetUrl(isEnglish ? "/en/search/" : "/search/"),
      },
      "query-input": "required name=search_term_string",
    },
  };
}

const staticRouteSeoEntries: SeoRouteEntry[] = [
  {
    pathPrefix: "/marketplace/responders",
    title: "Marketplace Hotline Detail — CALL ANYTHING",
    description: "查看指定 Responder 或 Hotline 的公开目录详情。",
    ogImagePath: "/og/marketplace.png",
    ogImageAlt: "CALL ANYTHING Marketplace 分享卡片",
    robots: "noindex,follow",
    prerender: false,
    includeInSitemap: false,
    ogType: "website",
    category: "marketplace",
  },
  {
    pathPrefix: "/docs/quick-start-caller",
    canonicalPath: "/docs/quick-start-caller/",
    title: "Quick Start for Callers — CALL ANYTHING",
    description: "5 分钟完成 Caller 侧本地接入，发起第一通 Hotline Call。",
    ogImagePath: "/og/quick-start-caller.png",
    ogImageAlt: "Quick Start for Callers 分享卡片",
    prerender: true,
    includeInSitemap: true,
    ogType: "article",
    category: "article",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Docs", path: "/docs/" },
      { name: "Quick Start for Callers", path: "/docs/quick-start-caller/" },
    ],
    changefreq: "monthly",
    priority: 0.8,
    alternates: QUICK_START_CALLER_MIRROR_ALTERNATES.zh,
  },
  {
    pathPrefix: "/en/docs/quick-start-caller",
    canonicalPath: "/en/docs/quick-start-caller/",
    title: "Caller Quick Start | CALL ANYTHING",
    description: "Run the first caller-side local loop, inspect one real result_package, and learn how a Hotline call works without deploying the full platform.",
    ogImagePath: "/og/quick-start-caller.png",
    ogImageAlt: "CALL ANYTHING caller quick start social card",
    prerender: true,
    includeInSitemap: false,
    ogType: "article",
    category: "article",
    locale: "en",
    breadcrumbs: [
      { name: "English Home", path: "/en/" },
      { name: "Docs", path: "/en/docs/" },
      { name: "Caller Quick Start", path: "/en/docs/quick-start-caller/" },
    ],
    changefreq: "monthly",
    priority: 0.7,
    alternates: QUICK_START_CALLER_MIRROR_ALTERNATES.en,
  },
  {
    pathPrefix: "/docs/quick-start-responder",
    canonicalPath: "/docs/quick-start-responder/",
    title: "Quick Start for Responders — CALL ANYTHING",
    description: "5 分钟完成 Responder 侧本地环境搭建，发布第一根 Hotline。",
    ogImagePath: "/og/quick-start-responder.png",
    ogImageAlt: "Quick Start for Responders 分享卡片",
    prerender: true,
    includeInSitemap: true,
    ogType: "article",
    category: "article",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Docs", path: "/docs/" },
      { name: "Quick Start for Responders", path: "/docs/quick-start-responder/" },
    ],
    changefreq: "monthly",
    priority: 0.8,
    alternates: QUICK_START_RESPONDER_MIRROR_ALTERNATES.zh,
  },
  {
    pathPrefix: "/en/docs/quick-start-responder",
    canonicalPath: "/en/docs/quick-start-responder/",
    title: "Responder Quick Start | CALL ANYTHING",
    description: "Create the first responder-side Hotline draft, validate the example capability, and understand the minimum review payload for publishing.",
    ogImagePath: "/og/quick-start-responder.png",
    ogImageAlt: "CALL ANYTHING responder quick start social card",
    prerender: true,
    includeInSitemap: false,
    ogType: "article",
    category: "article",
    locale: "en",
    breadcrumbs: [
      { name: "English Home", path: "/en/" },
      { name: "Docs", path: "/en/docs/" },
      { name: "Responder Quick Start", path: "/en/docs/quick-start-responder/" },
    ],
    changefreq: "monthly",
    priority: 0.7,
    alternates: QUICK_START_RESPONDER_MIRROR_ALTERNATES.en,
  },
  {
    pathPrefix: "/docs/protocol-examples",
    canonicalPath: "/docs/protocol-examples/",
    title: "协议最小示例 — CALL ANYTHING",
    description: "把最小 request、成功 result_package、错误返回和 hotline registration payload 收拢到一个稳定协议示例页。",
    ogImagePath: "/og/docs.png",
    ogImageAlt: "CALL ANYTHING 协议最小示例分享卡片",
    prerender: true,
    includeInSitemap: true,
    ogType: "article",
    category: "article",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Docs", path: "/docs/" },
      { name: "协议最小示例", path: "/docs/protocol-examples/" },
    ],
    changefreq: "monthly",
    priority: 0.8,
    alternates: PROTOCOL_EXAMPLES_MIRROR_ALTERNATES.zh,
  },
  {
    pathPrefix: "/en/docs/protocol-examples",
    canonicalPath: "/en/docs/protocol-examples/",
    title: "Protocol Examples | CALL ANYTHING",
    description:
      "Stable protocol shapes for a minimal request, successful result_package, common error response, and Hotline registration payload.",
    ogImagePath: "/og/docs.png",
    ogImageAlt: "CALL ANYTHING protocol examples social card",
    prerender: true,
    includeInSitemap: false,
    ogType: "article",
    category: "article",
    locale: "en",
    breadcrumbs: [
      { name: "English Home", path: "/en/" },
      { name: "Docs", path: "/en/docs/" },
      { name: "Protocol Examples", path: "/en/docs/protocol-examples/" },
    ],
    changefreq: "monthly",
    priority: 0.7,
    alternates: PROTOCOL_EXAMPLES_MIRROR_ALTERNATES.en,
  },
  {
    pathPrefix: "/docs/protocol-architecture",
    canonicalPath: "/docs/protocol-architecture/",
    title: "协议架构 — CALL ANYTHING",
    description: "把 Protocol、Client、Platform、Selfhost 以及 control plane / data plane 的边界单独讲清楚。",
    ogImagePath: "/og/docs.png",
    ogImageAlt: "CALL ANYTHING 协议架构分享卡片",
    prerender: true,
    includeInSitemap: true,
    ogType: "article",
    category: "article",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Docs", path: "/docs/" },
      { name: "协议架构", path: "/docs/protocol-architecture/" },
    ],
    changefreq: "monthly",
    priority: 0.8,
    alternates: PROTOCOL_ARCHITECTURE_MIRROR_ALTERNATES.zh,
  },
  {
    pathPrefix: "/en/docs/protocol-architecture",
    canonicalPath: "/en/docs/protocol-architecture/",
    title: "Protocol Architecture | CALL ANYTHING",
    description: "Understand how Protocol, Client, Platform, and Selfhost split control plane and data plane in CALL ANYTHING.",
    ogImagePath: "/og/docs.png",
    ogImageAlt: "CALL ANYTHING protocol architecture social card",
    prerender: true,
    includeInSitemap: false,
    ogType: "article",
    category: "article",
    locale: "en",
    breadcrumbs: [
      { name: "English Home", path: "/en/" },
      { name: "Docs", path: "/en/docs/" },
      { name: "Protocol Architecture", path: "/en/docs/protocol-architecture/" },
    ],
    changefreq: "monthly",
    priority: 0.6,
    alternates: PROTOCOL_ARCHITECTURE_MIRROR_ALTERNATES.en,
  },
  {
    pathPrefix: "/docs/deployability-profiles",
    canonicalPath: "/docs/deployability-profiles/",
    title: "部署 Profiles — CALL ANYTHING",
    description: "解释 Local Agent Loop、Selfhost Platform、Public Stack 和 Management Console 的启动命令、状态检查与安全边界。",
    ogImagePath: "/og/docs.png",
    ogImageAlt: "CALL ANYTHING 部署 Profiles 分享卡片",
    prerender: true,
    includeInSitemap: true,
    ogType: "article",
    category: "article",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Docs", path: "/docs/" },
      { name: "部署 Profiles", path: "/docs/deployability-profiles/" },
    ],
    changefreq: "monthly",
    priority: 0.8,
    alternates: DEPLOYABILITY_PROFILES_MIRROR_ALTERNATES.zh,
  },
  {
    pathPrefix: "/en/docs/deployability-profiles",
    canonicalPath: "/en/docs/deployability-profiles/",
    title: "Deployability Profiles | CALL ANYTHING",
    description:
      "Operator-readable deployment paths for Local Agent Loop, Selfhost Platform, Public Stack, Management Console, and their safety boundaries.",
    ogImagePath: "/og/docs.png",
    ogImageAlt: "CALL ANYTHING deployability profiles social card",
    prerender: true,
    includeInSitemap: false,
    ogType: "article",
    category: "article",
    locale: "en",
    breadcrumbs: [
      { name: "English Home", path: "/en/" },
      { name: "Docs", path: "/en/docs/" },
      { name: "Deployability Profiles", path: "/en/docs/deployability-profiles/" },
    ],
    changefreq: "monthly",
    priority: 0.6,
    alternates: DEPLOYABILITY_PROFILES_MIRROR_ALTERNATES.en,
  },
  // --- Phase 2 GEO content assets ----------------------------------------
  ...COMPARE_PAGES.map<SeoRouteEntry>((page) => ({
    pathPrefix: page.path.replace(/\/$/, ""),
    canonicalPath: page.path,
    title: page.title,
    description: page.description,
    ogImagePath: `/og/compare-${page.slug}.png`,
    ogImageAlt: `${page.title} 分享卡片`,
    prerender: true,
    includeInSitemap: true,
    ogType: "article",
    category: "compare",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Compare", path: "/compare/" },
      { name: page.eyebrow.replace(/^Compare · /, ""), path: page.path },
    ],
    changefreq: "monthly",
    priority: 0.8,
    alternates: mirrorAlternates(page.path, `/en/compare/${page.slug}/`).zh,
  })),
  ...EN_COMPARE_PAGES.map<SeoRouteEntry>((page) => ({
    pathPrefix: page.path.replace(/\/$/, ""),
    canonicalPath: page.path,
    title: page.title,
    description: page.description,
    ogImagePath: `/og/compare-${page.slug}.png`,
    ogImageAlt: `${page.title} social card`,
    prerender: true,
    includeInSitemap: false,
    ogType: "article",
    category: "compare",
    locale: "en",
    breadcrumbs: [
      { name: "English Home", path: "/en/" },
      { name: "Docs", path: "/en/docs/" },
      { name: page.otherName, path: page.path },
    ],
    changefreq: "monthly",
    priority: 0.6,
    alternates: mirrorAlternates(`/compare/${page.slug}/`, page.path).en,
  })),
  ...BLOG_POSTS.map<SeoRouteEntry>((post) => ({
    pathPrefix: post.path.replace(/\/$/, ""),
    canonicalPath: post.path,
    title: `${post.title} — CALL ANYTHING`,
    description: post.description,
    ogImagePath: post.ogImagePath ?? "/og/blog-default.png",
    ogImageAlt: `${post.title} 分享卡片`,
    prerender: true,
    includeInSitemap: true,
    ogType: "article",
    category: "blog-post",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Blog", path: "/blog/" },
      { name: post.title, path: post.path },
    ],
    changefreq: "monthly",
    priority: 0.7,
  })),
  ...EN_CASE_STUDIES.map<SeoRouteEntry>((study) => ({
    pathPrefix: study.path.replace(/\/$/, ""),
    canonicalPath: study.path,
    title: study.title,
    description: study.description,
    ogImagePath: "/og/blog-default.png",
    ogImageAlt: `${study.title} social card`,
    prerender: true,
    includeInSitemap: true,
    ogType: "article",
    category: "article",
    locale: "en",
    breadcrumbs: [
      { name: "English Home", path: "/en/" },
      { name: "Docs", path: "/en/docs/" },
      { name: "Case Study", path: study.path },
    ],
    changefreq: "monthly",
    priority: 0.6,
    alternates: [{ hreflang: "en", path: study.path }],
  })),
  {
    pathPrefix: "/blog",
    canonicalPath: "/blog/",
    title: "Blog — CALL ANYTHING",
    description: "OPC 经济、Hotline 协议设计、Marketplace 演进与超级个体经营笔记。",
    ogImagePath: "/og/blog-index.png",
    ogImageAlt: "CALL ANYTHING Blog 分享卡片",
    prerender: true,
    includeInSitemap: true,
    ogType: "website",
    category: "blog-index",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Blog", path: "/blog/" },
    ],
    changefreq: "weekly",
    priority: 0.7,
  },
  {
    pathPrefix: "/glossary",
    canonicalPath: "/glossary/",
    title: "Glossary — CALL ANYTHING",
    description:
      "CALL ANYTHING 术语表：Hotline / Caller / Responder / OPC / 数字分身 / Marketplace 等核心概念的标准定义与边界说明。",
    ogImagePath: "/og/glossary.png",
    ogImageAlt: "CALL ANYTHING Glossary 分享卡片",
    prerender: true,
    includeInSitemap: true,
    ogType: "website",
    category: "glossary",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Glossary", path: "/glossary/" },
    ],
    changefreq: "monthly",
    priority: 0.8,
    alternates: GLOSSARY_MIRROR_ALTERNATES.zh,
  },
  {
    pathPrefix: "/en/glossary",
    canonicalPath: "/en/glossary/",
    title: "Glossary | CALL ANYTHING",
    description:
      "Canonical English definitions for Hotline, Caller, Responder, OPC, Marketplace, result package, and other CALL ANYTHING terms.",
    ogImagePath: "/og/glossary.png",
    ogImageAlt: "CALL ANYTHING glossary social card",
    prerender: true,
    includeInSitemap: false,
    ogType: "website",
    category: "glossary",
    locale: "en",
    breadcrumbs: [
      { name: "English Home", path: "/en/" },
      { name: "Glossary", path: "/en/glossary/" },
    ],
    changefreq: "monthly",
    priority: 0.6,
    alternates: GLOSSARY_MIRROR_ALTERNATES.en,
  },
  {
    pathPrefix: "/faq",
    canonicalPath: "/faq/",
    title: "FAQ — CALL ANYTHING",
    description:
      "CALL ANYTHING 常见问题：商业化、安全信任、与 MCP/Skill/API 区别、协议接入、Marketplace 运营 30+ 条聚合解答。",
    ogImagePath: "/og/faq.png",
    ogImageAlt: "CALL ANYTHING FAQ 分享卡片",
    prerender: true,
    includeInSitemap: true,
    ogType: "website",
    category: "faq",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "FAQ", path: "/faq/" },
    ],
    changefreq: "monthly",
    priority: 0.8,
    alternates: FAQ_MIRROR_ALTERNATES.zh,
  },
  {
    pathPrefix: "/en/faq",
    canonicalPath: "/en/faq/",
    title: "FAQ | CALL ANYTHING",
    description:
      "English FAQ for CALL ANYTHING covering Hotline, MCP, Skills, APIs, seller-side packaging, trust, self-hosting, and publishing.",
    ogImagePath: "/og/faq.png",
    ogImageAlt: "CALL ANYTHING FAQ social card",
    prerender: true,
    includeInSitemap: false,
    ogType: "website",
    category: "faq",
    locale: "en",
    breadcrumbs: [
      { name: "English Home", path: "/en/" },
      { name: "FAQ", path: "/en/faq/" },
    ],
    changefreq: "monthly",
    priority: 0.6,
    alternates: FAQ_MIRROR_ALTERNATES.en,
  },
  {
    pathPrefix: "/trust-settlement",
    canonicalPath: "/trust-settlement/",
    title: "信任与结算 — CALL ANYTHING",
    description: "调用如何被计量、审计和保护",
    ogImagePath: "/og/pricing.png",
    ogImageAlt: "CALL ANYTHING 信任与结算分享卡片",
    prerender: true,
    includeInSitemap: true,
    ogType: "website",
    category: "pricing",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "信任与结算", path: "/trust-settlement/" },
    ],
    changefreq: "monthly",
    priority: 0.7,
    alternates: TRUST_SETTLEMENT_MIRROR_ALTERNATES.zh,
  },
  {
    pathPrefix: "/en/trust-settlement",
    canonicalPath: "/en/trust-settlement/",
    title: "Trust & Settlement — CALL ANYTHING",
    description: "How calls are metered, audited, and protected",
    ogImagePath: "/og/pricing.png",
    ogImageAlt: "CALL ANYTHING Trust & Settlement social card",
    prerender: true,
    includeInSitemap: false,
    ogType: "website",
    category: "pricing",
    locale: "en",
    breadcrumbs: [
      { name: "English Home", path: "/en/" },
      { name: "Trust & Settlement", path: "/en/trust-settlement/" },
    ],
    changefreq: "monthly",
    priority: 0.6,
    alternates: TRUST_SETTLEMENT_MIRROR_ALTERNATES.en,
  },
  {
    pathPrefix: "/vision",
    canonicalPath: "/vision/",
    title: "远期愿景 — CALL ANYTHING",
    description: "CALL ANYTHING 的长期方向和网络效应规划",
    ogImagePath: "/og/home.png",
    ogImageAlt: "CALL ANYTHING 远期愿景分享卡片",
    prerender: true,
    includeInSitemap: true,
    ogType: "website",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "远期愿景", path: "/vision/" },
    ],
    changefreq: "monthly",
    priority: 0.6,
    alternates: VISION_MIRROR_ALTERNATES.zh,
  },
  {
    pathPrefix: "/en/vision",
    canonicalPath: "/en/vision/",
    title: "Long-term Vision — CALL ANYTHING",
    description: "CALL ANYTHING's long-term direction and network effect planning",
    ogImagePath: "/og/home.png",
    ogImageAlt: "CALL ANYTHING Long-term Vision social card",
    prerender: true,
    includeInSitemap: false,
    ogType: "website",
    locale: "en",
    breadcrumbs: [
      { name: "English Home", path: "/en/" },
      { name: "Long-term Vision", path: "/en/vision/" },
    ],
    changefreq: "monthly",
    priority: 0.5,
    alternates: VISION_MIRROR_ALTERNATES.en,
  },
  {
    pathPrefix: "/opc",
    canonicalPath: "/opc/",
    title: "超级个体商业模式 — CALL ANYTHING",
    description: "OPC 如何通过 Hotline 实现能力变现",
    ogImagePath: "/og/home.png",
    ogImageAlt: "CALL ANYTHING 超级个体商业模式分享卡片",
    prerender: true,
    includeInSitemap: true,
    ogType: "website",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "超级个体商业模式", path: "/opc/" },
    ],
    changefreq: "monthly",
    priority: 0.6,
    alternates: OPC_MIRROR_ALTERNATES.zh,
  },
  {
    pathPrefix: "/en/opc",
    canonicalPath: "/en/opc/",
    title: "One-Person Company Model — CALL ANYTHING",
    description: "How OPCs monetize capabilities through Hotlines",
    ogImagePath: "/og/home.png",
    ogImageAlt: "CALL ANYTHING One-Person Company Model social card",
    prerender: true,
    includeInSitemap: false,
    ogType: "website",
    locale: "en",
    breadcrumbs: [
      { name: "English Home", path: "/en/" },
      { name: "One-Person Company Model", path: "/en/opc/" },
    ],
    changefreq: "monthly",
    priority: 0.5,
    alternates: OPC_MIRROR_ALTERNATES.en,
  },
  {
    pathPrefix: "/marketplace-roadmap",
    canonicalPath: "/marketplace-roadmap/",
    title: "Marketplace 路线图 — CALL ANYTHING",
    description: "从 Preview catalog 到公开发布的演进路径",
    ogImagePath: "/og/marketplace.png",
    ogImageAlt: "CALL ANYTHING Marketplace 路线图分享卡片",
    prerender: true,
    includeInSitemap: true,
    ogType: "website",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Marketplace 路线图", path: "/marketplace-roadmap/" },
    ],
    changefreq: "monthly",
    priority: 0.6,
    alternates: MARKETPLACE_ROADMAP_MIRROR_ALTERNATES.zh,
  },
  {
    pathPrefix: "/en/marketplace-roadmap",
    canonicalPath: "/en/marketplace-roadmap/",
    title: "Marketplace Roadmap — CALL ANYTHING",
    description: "Evolution path from preview catalog to public publishing",
    ogImagePath: "/og/marketplace.png",
    ogImageAlt: "CALL ANYTHING Marketplace Roadmap social card",
    prerender: true,
    includeInSitemap: false,
    ogType: "website",
    locale: "en",
    breadcrumbs: [
      { name: "English Home", path: "/en/" },
      { name: "Marketplace Roadmap", path: "/en/marketplace-roadmap/" },
    ],
    changefreq: "monthly",
    priority: 0.5,
    alternates: MARKETPLACE_ROADMAP_MIRROR_ALTERNATES.en,
  },
  {
    pathPrefix: "/pricing",
    canonicalPath: "/trust-settlement/",
    title: "信任与结算 — CALL ANYTHING",
    description: "调用如何被计量、审计和保护",
    robots: "noindex,follow",
    prerender: false,
    includeInSitemap: false,
    ogType: "website",
    category: "pricing",
  },
  {
    pathPrefix: "/marketplace",
    canonicalPath: "/marketplace/",
    title: "Marketplace — CALL ANYTHING",
    description: "浏览 Hotline、Responder 与能力分类，找到适合你任务的外部能力。",
    ogImagePath: "/og/marketplace.png",
    ogImageAlt: "CALL ANYTHING Marketplace 分享卡片",
    prerender: true,
    includeInSitemap: true,
    ogType: "website",
    category: "marketplace",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Marketplace", path: "/marketplace/" },
    ],
    changefreq: "weekly",
    priority: 0.9,
  },
  {
    pathPrefix: "/console",
    canonicalPath: "/console/",
    title: "Console — CALL ANYTHING",
    description: "查看 Caller / Responder 控制台原型，理解协议、审批、路由与可观测链路。",
    ogImagePath: "/og/console.png",
    ogImageAlt: "CALL ANYTHING Console 分享卡片",
    prerender: true,
    includeInSitemap: true,
    ogType: "website",
    category: "tooling",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Console", path: "/console/" },
    ],
    changefreq: "monthly",
    priority: 0.6,
  },
  {
    pathPrefix: "/playground",
    canonicalPath: "/playground/",
    title: "Playground — CALL ANYTHING",
    description: "浏览品牌系统、视觉原子与页面模块，查看产品表达方式。",
    ogImagePath: "/og/home.png",
    ogImageAlt: "CALL ANYTHING 品牌分享卡片",
    robots: "noindex,follow",
    prerender: true,
    includeInSitemap: false,
    ogType: "website",
    category: "tooling",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Playground", path: "/playground/" },
    ],
  },
  {
    pathPrefix: "/search",
    canonicalPath: "/search/",
    title: "站内搜索 — CALL ANYTHING",
    description: "在 CALL ANYTHING 的稳定页面里搜索 Hotline、result_package、Quick Start、FAQ 和协议示例。",
    ogImagePath: "/og/docs.png",
    ogImageAlt: "CALL ANYTHING 站内搜索分享卡片",
    robots: "noindex,follow",
    prerender: true,
    includeInSitemap: false,
    ogType: "website",
    category: "tooling",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Search", path: "/search/" },
    ],
    alternates: SEARCH_MIRROR_ALTERNATES.zh,
  },
  {
    pathPrefix: "/en/search",
    canonicalPath: "/en/search/",
    title: "Site Search | CALL ANYTHING",
    description: "Search the stable CALL ANYTHING pages for Hotline, result_package, quick starts, compare pages, and protocol examples.",
    ogImagePath: "/og/docs.png",
    ogImageAlt: "CALL ANYTHING site search social card",
    robots: "noindex,follow",
    prerender: true,
    includeInSitemap: false,
    ogType: "website",
    category: "tooling",
    locale: "en",
    breadcrumbs: [
      { name: "English Home", path: "/en/" },
      { name: "Search", path: "/en/search/" },
    ],
    alternates: SEARCH_MIRROR_ALTERNATES.en,
  },
  {
    pathPrefix: "/docs",
    canonicalPath: "/docs/",
    title: "Docs — CALL ANYTHING",
    description: "阅读快速开始、协议规范、Client 工作流与 Hotline 开发文档。",
    ogImagePath: "/og/docs.png",
    ogImageAlt: "CALL ANYTHING Docs 分享卡片",
    prerender: true,
    includeInSitemap: true,
    ogType: "website",
    category: "docs",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Docs", path: "/docs/" },
    ],
    changefreq: "weekly",
    priority: 0.9,
    alternates: DOCS_MIRROR_ALTERNATES.zh,
  },
  {
    pathPrefix: "/en/docs",
    canonicalPath: "/en/docs/",
    title: "Docs Overview | CALL ANYTHING",
    description:
      "English overview of the CALL ANYTHING protocol, capability model, glossary, compare pages, and current quick-start paths.",
    ogImagePath: "/og/docs.png",
    ogImageAlt: "CALL ANYTHING docs social card",
    prerender: true,
    includeInSitemap: false,
    ogType: "website",
    category: "docs",
    locale: "en",
    breadcrumbs: [
      { name: "English Home", path: "/en/" },
      { name: "Docs", path: "/en/docs/" },
    ],
    changefreq: "weekly",
    priority: 0.7,
    alternates: DOCS_MIRROR_ALTERNATES.en,
  },
  {
    pathPrefix: "/demo",
    canonicalPath: "/demo/",
    title: "Demo — CALL ANYTHING",
    description: "查看 CALL ANYTHING 的演示页面与交互样例。",
    ogImagePath: "/og/home.png",
    ogImageAlt: "CALL ANYTHING Demo 分享卡片",
    robots: "noindex,follow",
    prerender: true,
    includeInSitemap: false,
    ogType: "website",
    category: "demo",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Demo", path: "/demo/" },
    ],
  },
  {
    pathPrefix: "/",
    canonicalPath: "/",
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
    ogImagePath: "/og/home.png",
    ogImageAlt: DEFAULT_OG_IMAGE_ALT,
    robots: "index,follow",
    prerender: true,
    includeInSitemap: true,
    ogType: "website",
    category: "home",
    changefreq: "weekly",
    priority: 1.0,
    alternates: HOME_MIRROR_ALTERNATES.zh,
  },
  {
    pathPrefix: "/en",
    canonicalPath: "/en/",
    title: DEFAULT_EN_TITLE,
    description: DEFAULT_EN_DESCRIPTION,
    ogImagePath: "/og/home.png",
    ogImageAlt: "CALL ANYTHING English homepage social card",
    robots: "index,follow",
    prerender: true,
    includeInSitemap: false,
    ogType: "website",
    category: "home",
    locale: "en",
    changefreq: "weekly",
    priority: 0.7,
    alternates: HOME_MIRROR_ALTERNATES.en,
  },
];

const indexableMarketplaceDetailEntries: SeoRouteEntry[] = INDEXABLE_MARKETPLACE_DETAIL_ROUTES.map((entry) => ({
  pathPrefix: entry.path,
  canonicalPath: entry.path,
  title: entry.title,
  description: entry.description,
  ogImagePath: "/og/marketplace.png",
  ogImageAlt: "CALL ANYTHING Marketplace 详情分享卡片",
  robots: "index,follow",
  prerender: false,
  includeInSitemap: true,
  ogType: "website",
  category: "marketplace-detail",
  breadcrumbs: entry.breadcrumbs,
  changefreq: "weekly" as SitemapChangefreq,
  priority: 0.6,
}));

const routeSeoEntries = [...indexableMarketplaceDetailEntries, ...staticRouteSeoEntries];

function getDefaultSeoEntry(pathname: string): SeoRouteEntry {
  return {
    pathPrefix: pathname === "" ? "/" : pathname,
    canonicalPath: pathname === "/" ? "/" : pathname,
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
    ogImagePath: "/og/home.png",
    ogImageAlt: DEFAULT_OG_IMAGE_ALT,
    robots: "index,follow",
    prerender: false,
    includeInSitemap: false,
    ogType: "website",
    category: "tooling",
    locale: "zh-Hans",
  };
}

function resolveCanonicalPath(pathname: string, entry: SeoRouteEntry) {
  if (entry.canonicalPath) {
    return entry.canonicalPath;
  }

  const normalizedPath = normalizeMatchPath(pathname);

  if (entry.prerender) {
    return withTrailingSlash(normalizedPath);
  }

  return normalizedPath;
}

function buildOgImageUrl(pathname: string) {
  const url = new URL(pathname, SITE_ORIGIN);
  url.searchParams.set("v", OG_IMAGE_VERSION);
  return url.toString();
}

export function getRouteSeo(pathname: string): SeoRouteEntry {
  const normalizedPath = normalizeMatchPath(pathname);

  const exactMarketplaceDetail = getIndexableMarketplaceDetail(normalizedPath);
  if (exactMarketplaceDetail) {
    return (
      routeSeoEntries.find((entry) => entry.pathPrefix === exactMarketplaceDetail.path) ?? getDefaultSeoEntry(normalizedPath)
    );
  }

  return (
    routeSeoEntries.find(
      (entry) => normalizedPath === entry.pathPrefix || (entry.pathPrefix !== "/" && normalizedPath.startsWith(`${entry.pathPrefix}/`))
    ) ?? getDefaultSeoEntry(normalizedPath)
  );
}

function breadcrumbGraph(items: Array<{ name: string; path: string }>) {
  return {
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: new URL(item.path, SITE_ORIGIN).toString(),
    })),
  };
}

function faqGraph(items: Array<{ question: string; answer: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    inLanguage: "zh-Hans",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
}

function localizedFaqGraph(items: Array<{ question: string; answer: string }>, inLanguage: string) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    inLanguage,
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
}

function howToGraph(
  entry: SeoRouteEntry,
  canonicalUrl: string,
  steps: Array<{ name: string; text: string }>,
  inLanguage: string
) {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: entry.title,
    description: entry.description,
    url: canonicalUrl,
    inLanguage,
    step: steps.map((step, index) => ({
      "@type": "HowToStep",
      position: index + 1,
      name: step.name,
      text: step.text,
    })),
  };
}

function definedTermSetGraph(canonicalUrl: string, locale: RouteLocale): SeoStructuredDataNode {
  const setId = `${canonicalUrl}#defined-term-set`;
  const terms = locale === "en" ? EN_GLOSSARY_TERMS : GLOSSARY_TERMS;
  const inLanguage = locale === "en" ? "en" : "zh-Hans";
  const description =
    locale === "en"
      ? "Canonical definitions for Hotline, Caller, Responder, OPC, and related CALL ANYTHING concepts."
      : "CALL ANYTHING 术语表：Hotline、Caller、Responder、OPC 等核心概念的标准定义。";
  return {
    "@context": "https://schema.org",
    "@type": "DefinedTermSet",
    "@id": setId,
    name: "CALL ANYTHING Glossary",
    description,
    url: canonicalUrl,
    inLanguage,
    hasDefinedTerm: terms.map((term) => ({
      "@type": "DefinedTerm",
      "@id": `${canonicalUrl}#${term.id}`,
      name: term.term,
      alternateName: term.alias,
      description: term.definition,
      inDefinedTermSet: { "@id": setId },
      url: `${canonicalUrl}#${term.id}`,
    })),
  };
}

function pricingProductGraph(canonicalUrl: string): SeoStructuredDataNode {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "@id": `${canonicalUrl}#product`,
    name: "CALL ANYTHING — Hotline 协议",
    description: "通过 Hotline 协议把私域专长包装成 Agent 可付费调用的产品形态。",
    brand: { "@id": ORGANIZATION_ID, name: SITE_NAME },
    url: canonicalUrl,
    offers: {
      "@type": "Offer",
      url: canonicalUrl,
      price: "0",
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
      description: "Marketplace preview 期免费，正式收费后按调用次数协议级结算到 Responder 账户。",
    },
  };
}

function compareArticleGraph(page: ComparePage | EnglishComparePage, canonicalUrl: string, locale: RouteLocale): SeoStructuredDataNode {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": `${canonicalUrl}#article`,
    headline: page.title,
    description: page.description,
    url: canonicalUrl,
    inLanguage: locale === "en" ? "en" : "zh-Hans",
    author: { "@id": ORGANIZATION_ID },
    publisher: { "@id": ORGANIZATION_ID },
    mainEntityOfPage: { "@id": canonicalUrl },
    about: page.eyebrow,
  };
}

function blogIndexGraph(canonicalUrl: string): SeoStructuredDataNode {
  const posts = getOrderedBlogPosts();
  return {
    "@context": "https://schema.org",
    "@type": "Blog",
    "@id": `${canonicalUrl}#blog`,
    url: canonicalUrl,
    name: "CALL ANYTHING Blog",
    description: "OPC 经济、Hotline 协议设计与超级个体经营笔记。",
    inLanguage: "zh-Hans",
    publisher: { "@id": ORGANIZATION_ID },
    blogPost: posts.map((post) => ({
      "@type": "BlogPosting",
      "@id": new URL(post.path, SITE_ORIGIN).toString(),
      headline: post.title,
      url: new URL(post.path, SITE_ORIGIN).toString(),
      datePublished: post.date,
      author: { "@id": ORGANIZATION_ID, name: post.author },
    })),
  };
}

function blogPostingGraph(canonicalUrl: string): SeoStructuredDataNode | null {
  const path = new URL(canonicalUrl).pathname;
  const post = BLOG_POSTS.find((p) => p.path === path);
  if (!post) return null;
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": `${canonicalUrl}#blogposting`,
    headline: post.title,
    description: post.description,
    url: canonicalUrl,
    inLanguage: "zh-Hans",
    datePublished: post.date,
    dateModified: post.date,
    author: { "@id": ORGANIZATION_ID, name: post.author },
    publisher: { "@id": ORGANIZATION_ID },
    mainEntityOfPage: { "@id": canonicalUrl },
    keywords: post.tags.join(", "),
  };
}

function buildStructuredData(entry: SeoRouteEntry, canonicalUrl: string): SeoStructuredDataNode[] {
  const locale = entry.locale ?? "zh-Hans";
  const isEnglish = locale === "en";
  const languageTag = isEnglish ? "en" : "zh-Hans";
  const baseNodes: SeoStructuredDataNode[] = [];
  const pathname = new URL(canonicalUrl).pathname;
  const routeFaqs = isEnglish ? EN_ROUTE_FAQS[pathname] ?? [] : ROUTE_FAQS[pathname] ?? [];
  const routeHowToSteps = isEnglish ? EN_ROUTE_HOWTO_STEPS[pathname] ?? [] : ROUTE_HOWTO_STEPS[pathname] ?? [];

  if (entry.category === "home") {
    const homeNodes: SeoStructuredDataNode[] = [
      {
        "@context": "https://schema.org",
        "@graph": [
          {
            ...websiteGraph(languageTag),
          },
          {
            "@type": "Organization",
            "@id": ORGANIZATION_ID,
            name: SITE_NAME,
            url: `${SITE_ORIGIN}/`,
            logo: buildOgImageUrl("/og/home.png"),
          },
          {
            "@type": "SoftwareApplication",
            "@id": `${SITE_ORIGIN}/#application`,
            name: SITE_NAME,
            url: canonicalUrl,
            applicationCategory: "DeveloperApplication",
            operatingSystem: "Web",
            description: isEnglish
              ? "Lets AI agents discover, call, and settle external capabilities through standardized Hotlines."
              : "让任何 Agent 通过标准化 Hotline 协议发现、调用并结算外部能力。",
          },
        ],
      },
    ];

    if (routeFaqs.length > 0) {
      homeNodes.push(faqGraph(routeFaqs));
    } else if (isEnglish) {
      homeNodes.push(localizedFaqGraph(EN_FAQ_GROUPS[0]?.items ?? [], languageTag));
    }

    return homeNodes;
  }

  const webpageBase = {
    "@context": "https://schema.org",
    name: entry.title,
    url: canonicalUrl,
    description: entry.description,
    isPartOf: { "@id": WEBSITE_ID },
    inLanguage: languageTag,
  };

  switch (entry.category) {
    case "marketplace":
      baseNodes.push({
        ...webpageBase,
        "@type": "CollectionPage",
        about: "Marketplace hotline directory",
      });
      break;
    case "marketplace-detail":
      baseNodes.push({
        ...webpageBase,
        "@type": "WebPage",
        about: "Marketplace hotline detail",
      });
      break;
    case "docs":
      baseNodes.push({
        ...webpageBase,
        "@type": "CollectionPage",
        about: "Developer documentation",
      });
      break;
    case "article":
      baseNodes.push({
        ...webpageBase,
        "@type": "TechArticle",
        headline: entry.title,
        author: { "@id": ORGANIZATION_ID },
        publisher: { "@id": ORGANIZATION_ID },
      });
      break;
    case "glossary":
      baseNodes.push({
        ...webpageBase,
        "@type": "CollectionPage",
        about: "Glossary of CALL ANYTHING terms",
      });
      baseNodes.push(definedTermSetGraph(canonicalUrl, locale));
      break;
    case "faq":
      baseNodes.push({
        ...webpageBase,
        "@type": "FAQPage",
        about: "Aggregated frequently asked questions",
      });
      baseNodes.push(localizedFaqGraph(isEnglish ? EN_ALL_FAQS : ALL_FAQS, languageTag));
      break;
    case "pricing":
      baseNodes.push({
        ...webpageBase,
        "@type": "WebPage",
        about: "Pricing and settlement",
      });
      baseNodes.push(pricingProductGraph(canonicalUrl));
      break;
    case "compare": {
      const comparePage = isEnglish
        ? EN_COMPARE_PAGES.find((p) => p.path === pathname)
        : COMPARE_PAGES.find((p) => p.path === pathname);
      if (comparePage) {
        baseNodes.push(compareArticleGraph(comparePage, canonicalUrl, locale));
        baseNodes.push(localizedFaqGraph(comparePage.faqs, languageTag));
      } else {
        baseNodes.push({
          ...webpageBase,
          "@type": "Article",
        });
      }
      break;
    }
    case "blog-index":
      baseNodes.push({
        ...webpageBase,
        "@type": "CollectionPage",
        about: "Blog index",
      });
      baseNodes.push(blogIndexGraph(canonicalUrl));
      break;
    case "blog-post": {
      const blogPosting = blogPostingGraph(canonicalUrl);
      if (blogPosting) {
        baseNodes.push(blogPosting);
      } else {
        baseNodes.push({
          ...webpageBase,
          "@type": "Article",
        });
      }
      break;
    }
    case "demo":
    case "tooling":
      baseNodes.push({
        ...webpageBase,
        "@type": "WebPage",
      });
      break;
    default:
      baseNodes.push({
        ...webpageBase,
        "@type": "WebPage",
      });
      break;
  }

  if (entry.breadcrumbs?.length) {
    baseNodes.push({
      "@context": "https://schema.org",
      ...breadcrumbGraph(entry.breadcrumbs),
    });
  }

  if (entry.category !== "compare" && entry.category !== "faq" && routeFaqs.length > 0) {
    baseNodes.push(localizedFaqGraph(routeFaqs, languageTag));
  }

  if (routeHowToSteps.length > 0) {
    baseNodes.push(howToGraph(entry, canonicalUrl, routeHowToSteps, languageTag));
  }

  return baseNodes;
}

type SitemapEntry = {
  path: string;
  changefreq?: SitemapChangefreq;
  priority?: number;
  alternates?: AlternateLink[];
};

function getSitemapEntries(): SitemapEntry[] {
  return routeSeoEntries
    .filter((entry) => entry.includeInSitemap)
    .map((entry) => {
      const path = resolveCanonicalPath(entry.pathPrefix, entry);
      const alternates = getAlternateLinks(entry, path);
      // sitemap 里只在真正存在多语言版本时（不只有 zh-Hans + x-default）才注入 xhtml:link
      const hasRealLocaleAlternate = alternates.some(
        (a) => a.hreflang !== "zh-Hans" && a.hreflang !== "x-default"
      );
      return {
        path,
        changefreq: entry.changefreq,
        priority: entry.priority,
        alternates: hasRealLocaleAlternate ? alternates : undefined,
      };
    });
}

export const PRERENDER_ROUTES = routeSeoEntries.filter((entry) => entry.prerender).map((entry) => entry.pathPrefix);
export const SITEMAP_ROUTES = getSitemapEntries().map((entry) => entry.path);
const SITEMAP_ENTRIES = getSitemapEntries();

function getAlternateLinks(entry: SeoRouteEntry, canonicalPath: string): AlternateLink[] {
  const locale = entry.locale ?? "zh-Hans";
  const declared = entry.alternates ?? [];
  const seen = new Set<string>(declared.map((a) => a.hreflang));
  const merged: AlternateLink[] = declared.map((a) => ({
    hreflang: a.hreflang,
    href: new URL(a.path, SITE_ORIGIN).toString(),
  }));

  if (locale === "en") {
    if (!seen.has("en")) {
      merged.unshift({ hreflang: "en", href: new URL(canonicalPath, SITE_ORIGIN).toString() });
    }
    return merged;
  }

  // 永远确保 zh-Hans 与 x-default 至少指向自己，便于搜索引擎识别为简体中文权威版本。
  if (!seen.has("zh-Hans")) {
    merged.push({ hreflang: "zh-Hans", href: new URL(canonicalPath, SITE_ORIGIN).toString() });
  }
  if (!seen.has("x-default")) {
    merged.push({ hreflang: "x-default", href: new URL(canonicalPath, SITE_ORIGIN).toString() });
  }

  return merged;
}

export function getRouteSeoDocument(pathname: string): RouteSeoDocument {
  const normalizedPath = normalizeMatchPath(pathname);
  const seo = getRouteSeo(normalizedPath);
  const canonicalPath = resolveCanonicalPath(normalizedPath, seo);
  const canonicalUrl = new URL(canonicalPath, SITE_ORIGIN).toString();
  const locale = seo.locale ?? "zh-Hans";

  return {
    title: seo.title,
    description: seo.description,
    canonicalUrl,
    htmlLang: locale === "en" ? "en" : "zh-Hans",
    ogLocale: locale === "en" ? "en_US" : "zh_CN",
    ogImageUrl: buildOgImageUrl(seo.ogImagePath ?? "/og/home.png"),
    ogImageAlt: seo.ogImageAlt ?? DEFAULT_OG_IMAGE_ALT,
    ogType: seo.ogType ?? "website",
    robots: seo.robots ?? "index,follow",
    structuredData: JSON.stringify(buildStructuredData(seo, canonicalUrl)),
    alternateLinks: getAlternateLinks(seo, canonicalPath),
  };
}

function escapeXml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

export function buildSitemapXml(lastModifiedIso: string) {
  const normalizedLastmod = lastModifiedIso.slice(0, 10);
  const hasAnyAlternates = SITEMAP_ENTRIES.some((entry) => entry.alternates?.length);

  const urls = SITEMAP_ENTRIES.map((entry) => {
    const lines = [
      "  <url>",
      `    <loc>${escapeXml(new URL(entry.path, SITE_ORIGIN).toString())}</loc>`,
      `    <lastmod>${normalizedLastmod}</lastmod>`,
    ];
    if (entry.changefreq) {
      lines.push(`    <changefreq>${entry.changefreq}</changefreq>`);
    }
    if (typeof entry.priority === "number") {
      lines.push(`    <priority>${entry.priority.toFixed(1)}</priority>`);
    }
    if (entry.alternates?.length) {
      for (const alt of entry.alternates) {
        lines.push(
          `    <xhtml:link rel="alternate" hreflang="${escapeXml(alt.hreflang)}" href="${escapeXml(alt.href)}" />`
        );
      }
    }
    lines.push("  </url>");
    return lines.join("\n");
  }).join("\n");

  const urlsetAttrs = ['xmlns="http://www.sitemaps.org/schemas/sitemap/0.9"'];
  if (hasAnyAlternates) {
    urlsetAttrs.push('xmlns:xhtml="http://www.w3.org/1999/xhtml"');
  }

  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    `<urlset ${urlsetAttrs.join(" ")}>`,
    urls,
    "</urlset>",
    "",
  ].join("\n");
}

// 公开 surface 不暴露的内部页面，所有 user-agent 一律 disallow
const ROBOTS_DISALLOW_PATHS = ["/demo", "/playground", "/marketplace/responders/"];

// 全部允许收录的 AI 爬虫白名单。决策依据见 docs/seo-geo-playbook.md §1。
// 如果未来要拒绝某个具体 bot，只需将其从此列表移到下方 disallow 列表即可。
const ROBOTS_ALLOW_AI_BOTS = [
  "GPTBot",
  "OAI-SearchBot",
  "ChatGPT-User",
  "ClaudeBot",
  "Claude-Web",
  "anthropic-ai",
  "PerplexityBot",
  "Perplexity-User",
  "Google-Extended",
  "Applebot-Extended",
  "Amazonbot",
  "Bytespider",
  "Baiduspider",
  "360Spider",
  "YisouSpider",
  "Sogou web spider",
  "CCBot",
];

export function buildRobotsTxt() {
  const disallowLines = ROBOTS_DISALLOW_PATHS.map((path) => `Disallow: ${path}`).join("\n");

  const defaultBlock = ["# Default: allow general crawlers, disallow private surfaces", "User-agent: *", "Allow: /", disallowLines].join(
    "\n"
  );

  const aiBotBlocks = ROBOTS_ALLOW_AI_BOTS.map((bot) => `User-agent: ${bot}\nAllow: /`).join("\n\n");

  return [
    "# CALL ANYTHING - public site",
    "# Generated by buildRobotsTxt() in src/app/seo.ts",
    "",
    defaultBlock,
    "",
    "# AI training & retrieval crawlers (all allowed by policy)",
    aiBotBlocks,
    "",
    `Sitemap: ${SITE_ORIGIN}/sitemap.xml`,
    "",
  ].join("\n");
}

export { buildMarketplaceDetailExceptionsNginx };

/**
 * RSS 2.0 feed for /blog. Generated at build time, served at /blog/feed.xml.
 * Consumed by Perplexity, Feedly, Reeder, Cursor's web reader, etc.
 */
export function buildBlogFeedXml(buildIso: string): string {
  const posts = getOrderedBlogPosts();
  const lastBuildDate = new Date(buildIso).toUTCString();
  const channelLink = `${SITE_ORIGIN}/blog/`;
  const feedLink = `${SITE_ORIGIN}/blog/feed.xml`;

  const items = posts.map((post) => {
    const url = new URL(post.path, SITE_ORIGIN).toString();
    const pubDate = new Date(`${post.date}T00:00:00Z`).toUTCString();
    return [
      "    <item>",
      `      <title><![CDATA[${post.title}]]></title>`,
      `      <link>${url}</link>`,
      `      <guid isPermaLink="true">${url}</guid>`,
      `      <pubDate>${pubDate}</pubDate>`,
      `      <description><![CDATA[${post.description}]]></description>`,
      `      <content:encoded><![CDATA[${post.tldr}]]></content:encoded>`,
      ...post.tags.map((tag) => `      <category>${tag}</category>`),
      "    </item>",
    ].join("\n");
  });

  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<rss version="2.0" xmlns:atom="http://www.w3.org/2005/Atom" xmlns:content="http://purl.org/rss/1.0/modules/content/">',
    "  <channel>",
    `    <title>CALL ANYTHING Blog</title>`,
    `    <link>${channelLink}</link>`,
    `    <description>OPC 经济、Hotline 协议设计与超级个体经营笔记。</description>`,
    `    <language>zh-Hans</language>`,
    `    <lastBuildDate>${lastBuildDate}</lastBuildDate>`,
    `    <atom:link href="${feedLink}" rel="self" type="application/rss+xml" />`,
    items.join("\n"),
    "  </channel>",
    "</rss>",
    "",
  ].join("\n");
}
